import { FC, ReactNode } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import NavMenu from '@/components/nav/NavMenu.tsx';

import useAuth from '@/hooks/useAuth.ts';

import FeedPage from '@/pages/FeedPage.tsx';
import HomePage from '@/pages/HomePage.tsx';
import LoadingPage from '@/pages/LoadingPage.tsx';
import LoginPage from '@/pages/LoginPage.tsx';
import ProfilePage from '@/pages/ProfilePage.tsx';
import SearchPage from '@/pages/SearchPage.tsx';

import { useAuthStore } from '@/stores/authStore.ts';

// Protected route component that redirects to log in if not authenticated
const ProtectedRoute: FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser } = useAuth();
    return authUser ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public route component that redirects to home if already authenticated
const PublicRoute: FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser } = useAuth();
    return authUser ? <Navigate to="/" replace /> : <>{children}</>;
};

// Layout component that includes the NavMenu
const AppLayout: FC = () => {
    const { authUser } = useAuth();

    return (
        <>
            <Outlet />
            {authUser && <NavMenu />}
        </>
    );
};

const Router: FC = () => {
    const { authUser } = useAuth();
    const isUserSet = useAuthStore((state) => state.isUserSet);

    if (!isUserSet) {
        return <LoadingPage />;
    }

    const router = createBrowserRouter([
        {
            path: '*',
            element: <AppLayout />,
            children: [
                {
                    path: '',
                    element: (
                        <ProtectedRoute>
                            <HomePage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'feed',
                    element: (
                        <ProtectedRoute>
                            <FeedPage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'search',
                    element: (
                        <ProtectedRoute>
                            <SearchPage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'profile',
                    element: (
                        <ProtectedRoute>
                            <ProfilePage />
                        </ProtectedRoute>
                    ),
                },
                {
                    path: 'login',
                    element: (
                        <PublicRoute>
                            <LoginPage />
                        </PublicRoute>
                    ),
                },
                {
                    path: '*',
                    element: <Navigate to={authUser ? '/' : '/login'} replace />,
                },
            ],
        },
    ]);

    return <RouterProvider router={router} />;
};

export default Router;
